import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';

@Component({
  selector: 'app-pending-assessments',
  templateUrl: './pending-assessments.component.html',
  styleUrls: ['./pending-assessments.component.scss']
})
export class PendingAssessmentsComponent implements OnInit {
  assessments: any;
  sortedAssessments: any[] = [];
  filteredAssessments: any[] = [];
  selectedFilter: 'ALL' | 'NEW' | 'IN_PROGRESS' | 'COMPLETED' = 'ALL';

  constructor(private storage: StorageMap, private router: Router) {
    this.assessments = [];
  }

  ngOnInit(): void {
    this.storage.get('assessments').subscribe((data: any) => {
      this.assessments = data;
      if (Array.isArray(this.assessments?.assessments)) {
        this.sortedAssessments = [...this.assessments.assessments]
          .filter(a => !!a?.invitedOn)
          .sort((a, b) => {
            // Latest invite first
            const aTime = new Date(a.invitedOn).getTime();
            const bTime = new Date(b.invitedOn).getTime();
            return bTime - aTime;
          });
        this.applyFilter();
      }
    });
  }

  gotoWelcomePage(assessmentId) {
    this.router.navigate([`candidate/candidate-welcome/${assessmentId}`]);
  }

  getInitials(): string {
    const name: string = (this.assessments?.candidateName || '').trim();
    const email: string = (this.assessments?.email || '').trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const first = parts[0]?.charAt(0) || '';
      const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) : (email?.charAt(0) || '');
      return `${first}${last}`;
    }
    return (email || '  ').slice(0, 2);
  }

  getChipClass(expiresAt?: string): string {
    if (!expiresAt) return 'chip-neutral';
    const msRemaining = new Date(expiresAt).getTime() - Date.now();
    const day = 24 * 60 * 60 * 1000;
    if (msRemaining <= day) return 'chip-danger';
    if (msRemaining <= 3 * day) return 'chip-warn';
    return 'chip-neutral';
  }

  getProgressChipClass(status?: string): string {
    const normalized = (status || '').toUpperCase();
    if (normalized === 'COMPLETED') return 'chip-completed';
    if (normalized === 'IN_PROGRESS') return 'chip-progress';
    return 'chip-new';
  }

  getProgressIcon(status?: string): string {
    const normalized = (status || '').toUpperCase();
    if (normalized === 'COMPLETED') return 'check_circle';
    if (normalized === 'IN_PROGRESS') return 'hourglass_top';
    return 'fiber_new';
  }

  trackByAssessmentId = (_: number, item: any) => item?.assessmentId || item?.id || _;

  setFilter(filter: 'ALL' | 'NEW' | 'IN_PROGRESS' | 'COMPLETED') {
    this.selectedFilter = filter;
    this.applyFilter();
  }

  private applyFilter() {
    const list = this.selectedFilter === 'ALL'
      ? this.sortedAssessments
      : this.sortedAssessments.filter(a => (a?.status || '').toUpperCase() === this.selectedFilter);

    this.filteredAssessments = [...list].sort((a, b) => {
      const orderDiff = this.getStatusOrder((a?.status || '').toUpperCase()) - this.getStatusOrder((b?.status || '').toUpperCase());
      if (orderDiff !== 0) return orderDiff;
      const aTime = a?.invitedOn ? new Date(a.invitedOn).getTime() : 0;
      const bTime = b?.invitedOn ? new Date(b.invitedOn).getTime() : 0;
      return bTime - aTime; // latest first within the same status
    });
  }

  private getStatusOrder(status: string): number {
    switch (status) {
      case 'NEW':
        return 0;
      case 'IN_PROGRESS':
        return 1;
      case 'COMPLETED':
        return 2;
      default:
        return 3;
    }
  }

  getCardClass(status?: string): string {
    const normalized = (status || '').toUpperCase();
    if (normalized === 'COMPLETED') return 'card-completed';
    if (normalized === 'IN_PROGRESS') return 'card-in-progress';
    return 'card-new';
  }

  getActionButtonClass(status?: string): string {
    const normalized = (status || '').toUpperCase();
    if (normalized === 'COMPLETED') return 'btn-submitted';
    if (normalized === 'IN_PROGRESS') return 'btn-continue';
    return 'get-start-btn';
  }

  getActionButtonLabel(status?: string): string {
    const normalized = (status || '').toUpperCase();
    if (normalized === 'COMPLETED') return 'Submitted';
    if (normalized === 'IN_PROGRESS') return 'Continue';
    return 'Get Started';
  }

  onActionClick(assessment: any) {
    const normalized = (assessment?.status || '').toUpperCase();
    if (normalized === 'COMPLETED') {
      return; // No action for submitted
    }
    if (normalized === 'IN_PROGRESS') {
      this.router.navigate(['candidate/assessment-categories'], { queryParams: { id: assessment.assessmentId } });
      return;
    }
    // NEW → welcome screen
    this.gotoWelcomePage(assessment.assessmentId);
  }

}
